import { graphql } from 'gatsby';
import { usePageDataset } from '../../../templates/PageDatasetsContext';
import {
    KontentMedia,
    KontentStringValue,
} from '../../../../types/KontentProps';
import {
    ProductDetailMastheadData,
    ProductDescription,
    ProductSpecifications,
} from '../../../templates/StructuredDataContext';

export interface wellnessType {
    value: {
        name: string;
        codename: string;
    }[];
}

export interface ProductPreviewData {
    __typename: string;
    display_headline: KontentStringValue;
    elements:
        | ProductDetailMastheadData['elements']
        | ProductSpecifications['elements']
        | ProductDescription['elements'];
    detail_masthead: ProductDetailMastheadData;
    product_specifications: ProductSpecifications;
    product_description: ProductDescription;
    product_display_name?: KontentStringValue;
    sub_title?: KontentStringValue;
    short_description?: KontentStringValue;
    description_image?: KontentMedia;
    gallery_images: {
        value: {
            file: {
                value: {
                    description: string;
                    url: string;
                    name: string;
                }[];
            };
        }[];
    };
}

export interface ProductPagePreview {
    id?: Key | null | undefined;
    elements: {
        slug: {
            value: string;
            url?: string;
        };
        display_page_name: KontentStringValue;
        page_search_image: KontentMedia;
        page_search_summary: KontentStringValue;
        page_components: {
            value: ProductPreviewData[];
        };
        wellness_type: wellnessType;
        wellness_type___de_de: wellnessType;
        wellness_type___fr_fr: wellnessType;
    };
    preferred_language?: string;
}

interface ProductPageQuery {
    allKontentItemLayoutMasterPage: {
        nodes: ProductPagePreview[];
    };
}

export const fragmentProductPagePreview = graphql`
    fragment productPagePreview on kontent_item_layout___master_page {
        id
        elements {
            wellness_type {
                value {
                    codename
                    name
                }
            }
            wellness_type___de_de {
                value {
                    codename
                    name
                }
            }
            wellness_type___fr_fr {
                value {
                    codename
                    name
                }
            }
            slug {
                value
                url
            }
            display_page_name {
                value
            }
            page_search_image {
                ...kontentImage
            }
            page_search_summary {
                value
            }
            page_components {
                value {
                    __typename
                    ...kontentProductDetailMasthead
                    ...ProductSpecifications
                    ...kontentProductDescription
                }
            }
        }
        preferred_language
    }
`;

// Only the fields the sauna quizzes read (parseProducts in SaunaQuizResults
// and WhatSaunaResults); productPagePreview carries whole product mastheads.
export const fragmentQuizProductPreview = graphql`
    fragment quizProductPreview on kontent_item_layout___master_page {
        id
        elements {
            wellness_type {
                value {
                    codename
                    name
                }
            }
            wellness_type___de_de {
                value {
                    codename
                    name
                }
            }
            wellness_type___fr_fr {
                value {
                    codename
                    name
                }
            }
            slug {
                value
                url
            }
            display_page_name {
                value
            }
            page_components {
                value {
                    __typename
                    ... on kontent_item_component___product_description {
                        elements {
                            short_description {
                                value
                            }
                        }
                    }
                    ... on kontent_item_component___product_specifications {
                        elements {
                            gallery_images {
                                value {
                                    ... on kontent_item_media___image {
                                        elements {
                                            file {
                                                value {
                                                    url
                                                    description
                                                    name
                                                }
                                            }
                                        }
                                    }
                                    ... on kontent_item_media___product_image {
                                        elements {
                                            file {
                                                value {
                                                    url
                                                    description
                                                    name
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            specs {
                                value {
                                    ... on kontent_item_data___sauna_specifications {
                                        elements {
                                            capacity {
                                                value {
                                                    ... on kontent_item_data___capacity {
                                                        elements {
                                                            capacity_value {
                                                                value
                                                            }
                                                            capacity_tag {
                                                                value {
                                                                    codename
                                                                }
                                                            }
                                                            capacity_tag___de_de {
                                                                value {
                                                                    codename
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        preferred_language
    }
`;

export const useProducts = (): ProductPagePreview[] => {
    const productPages: ProductPageQuery['allKontentItemLayoutMasterPage'] =
        usePageDataset('pageProducts');

    return productPages?.nodes ?? [];
};
